import { FilterTypesEnum } from "../filter-types.ts";
import type { KpiBreakdownData } from "../kpi-api-types.ts";
import {
  type BreakdownFilterInput,
  fetchChannelKpiBreakdownWithScope,
} from "./channel-kpi-breakdown.ts";
import type { TracifyApiBasePath } from "./tracify-api-client.ts";
import {
  type AnalyticsScope,
  createAnalyticsScopeFromTracifyContext,
} from "./scope.ts";
import type { DateScopeOverrideInput } from "./date-scope.ts";
import type { TracifyContext } from "../ai/request-context.ts";

export const MAX_CREATIVE_VISUALS = 3;

export type CreativeVisual = {
  creative_id: string;
  ad_id: string | null;
  ad_name: string | null;
  image_url: string | null;
  video_url: string | null;
  metrics: {
    total_spend: number | null;
    customer_revenue: number | null;
    return_on_ad_spend: number | null;
    clicks: number | null;
    impressions: number | null;
    video_view: number | null;
  };
};

export type CreativeVisualsSuccess = {
  channel: string;
  requested_creative_ids: string[];
  creatives: CreativeVisual[];
  missing_creative_ids: string[];
};

type FilterClause = {
  name: string;
  operator: FilterTypesEnum;
  value: unknown;
};

export type CreativeVisualsFetchInput = {
  channel: string;
  creative_ids: string[];
  filters?: string | null;
};

export type FetchCreativeVisualsOptions = {
  baseUrl: string;
  scope: AnalyticsScope;
  input: CreativeVisualsFetchInput;
  token: string;
  timeoutMs?: number;
  maxRetries?: number;
  retryDelayMs?: number;
  requestId?: string;
  apiBasePath?: TracifyApiBasePath;
};

export type FetchCreativeVisualsWithContextOptions = Omit<
  FetchCreativeVisualsOptions,
  "scope" | "input"
> & {
  context: TracifyContext;
  input: CreativeVisualsFetchInput & DateScopeOverrideInput;
};

const HTTP_URL_PATTERN = /^https?:\/\//i;

export function normalizeCreativeIds(creativeIds: string[]): string[] {
  const seen = new Set<string>();
  const deduped: string[] = [];

  for (const creativeId of creativeIds) {
    const trimmed = creativeId.trim();
    if (trimmed.length === 0 || seen.has(trimmed)) {
      continue;
    }

    seen.add(trimmed);
    deduped.push(trimmed);
  }

  return deduped.slice(0, MAX_CREATIVE_VISUALS);
}

function normalizeAssetUrl(value: string | null | undefined): string | null {
  if (typeof value !== "string") {
    return null;
  }

  const trimmed = value.trim();
  return HTTP_URL_PATTERN.test(trimmed) ? trimmed : null;
}

export function parseCreativeVisualFilters(
  filters: string | null | undefined
): FilterClause[] {
  if (!filters || filters.trim().length === 0) {
    return [];
  }

  const parsed = JSON.parse(filters);
  if (!Array.isArray(parsed)) {
    throw new Error(
      "Creative visual lookup requires array-based scope filters when filters are present."
    );
  }

  return parsed as FilterClause[];
}

function buildCreativeMetrics(
  row: KpiBreakdownData
): CreativeVisual["metrics"] {
  return {
    total_spend: row.total?.total_spend ?? null,
    customer_revenue: row.total?.customer_revenue ?? null,
    return_on_ad_spend: row.total?.return_on_ad_spend ?? null,
    clicks: row.fixed?.clicks ?? null,
    impressions: row.fixed?.impressions ?? null,
    video_view: row.fixed?.video_view ?? null,
  };
}

export function buildCreativeVisual(
  row: KpiBreakdownData
): CreativeVisual | null {
  const creativeId = row.info.ad_creative_id?.trim();
  if (!creativeId) {
    return null;
  }

  return {
    creative_id: creativeId,
    ad_id: row.info.ad_id?.trim() || null,
    ad_name: row.info.ad_name?.trim() || null,
    image_url: normalizeAssetUrl(row.info.image),
    video_url: normalizeAssetUrl(row.info.video),
    metrics: buildCreativeMetrics(row),
  };
}

export async function fetchCreativeVisualsWithScope({
  baseUrl,
  scope,
  input,
  token,
  timeoutMs,
  maxRetries,
  retryDelayMs,
  requestId,
  apiBasePath,
}: FetchCreativeVisualsOptions): Promise<CreativeVisualsSuccess> {
  const creativeIds = normalizeCreativeIds(input.creative_ids);
  if (creativeIds.length === 0) {
    throw new Error(
      "creative_ids must include at least one non-empty creative id."
    );
  }

  const combinedFilters: NonNullable<BreakdownFilterInput> = [
    ...parseCreativeVisualFilters(input.filters ?? scope.filters),
    {
      name: "ad_creative_id",
      operator: FilterTypesEnum.in,
      value: creativeIds,
    },
  ];
  const response = await fetchChannelKpiBreakdownWithScope({
    baseUrl,
    scope: {
      ...scope,
      filters: null,
      topN: creativeIds.length,
    },
    input: {
      channel: input.channel,
      breakdown: "creative",
      filters: combinedFilters,
      limit: creativeIds.length,
      offset: 0,
    },
    token,
    timeoutMs,
    maxRetries,
    retryDelayMs,
    requestId,
    apiBasePath,
  });
  const visualById = new Map<string, CreativeVisual>();

  for (const row of response.breakdown.data) {
    const visual = buildCreativeVisual(row);
    if (!visual || visualById.has(visual.creative_id)) {
      continue;
    }

    visualById.set(visual.creative_id, visual);
  }

  const creatives = creativeIds
    .map((creativeId) => visualById.get(creativeId) ?? null)
    .filter((visual): visual is CreativeVisual => visual !== null);
  const missingCreativeIds = creativeIds.filter(
    (creativeId) => !visualById.has(creativeId)
  );

  return {
    channel: response.channel,
    requested_creative_ids: creativeIds,
    creatives,
    missing_creative_ids: missingCreativeIds,
  };
}

export async function fetchCreativeVisualsWithContext({
  context,
  input,
  ...options
}: FetchCreativeVisualsWithContextOptions): Promise<CreativeVisualsSuccess> {
  return fetchCreativeVisualsWithScope({
    ...options,
    scope: createAnalyticsScopeFromTracifyContext(context, input),
    input,
  });
}
